/** Shared by the wind and precipitation overlays: one regular WGS84 grid, row-major from the SW. */
export interface WeatherGrid {
  west: number;
  south: number;
  dLon: number;
  dLat: number;
  nx: number;
  ny: number;
}

function cell(a: readonly (number | null)[], nx: number, i: number, j: number) {
  return a[j * nx + i] ?? null;
}

/**
 * Bilinear sample of one hour's values (index = j·nx + i) at (lon, lat), in the values' own
 * units. Null outside the grid or when any of the four corners is null (draw nothing there).
 */
export function gridBilinear(
  grid: WeatherGrid,
  a: readonly (number | null)[],
  lon: number,
  lat: number,
): number | null {
  const x = (lon - grid.west) / grid.dLon;
  const y = (lat - grid.south) / grid.dLat;
  // A hair of tolerance so the last grid row/column (float sums of dLon/dLat) is inside.
  const e = 1e-9;
  if (!(x >= -e && y >= -e && x <= grid.nx - 1 + e && y <= grid.ny - 1 + e)) return null;
  const i0 = Math.max(0, Math.min(Math.floor(x), grid.nx - 2));
  const j0 = Math.max(0, Math.min(Math.floor(y), grid.ny - 2));
  const fx = Math.min(1, Math.max(0, x - i0));
  const fy = Math.min(1, Math.max(0, y - j0));
  const c00 = cell(a, grid.nx, i0, j0);
  const c10 = cell(a, grid.nx, i0 + 1, j0);
  const c01 = cell(a, grid.nx, i0, j0 + 1);
  const c11 = cell(a, grid.nx, i0 + 1, j0 + 1);
  if (c00 === null || c10 === null || c01 === null || c11 === null) return null;
  return c00 * (1 - fx) * (1 - fy) + c10 * fx * (1 - fy) + c01 * (1 - fx) * fy + c11 * fx * fy;
}
